import { AppDispatcher } from 'dispatcher';
import cloneDeep from 'lodash/cloneDeep';
import concat from 'lodash/concat';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import unset from 'lodash/unset';
import assign from 'object-assign';
import { LocationStore } from 'stores/LocationStore';
import * as CameraUtil from 'utils/CameraUtil';
import CommonUtil from 'utils/CommonUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import LocaleUtil, { getLocale } from 'utils/LocaleUtil';
import * as constants from 'vd-constants';
import { BaseStore } from './BaseStore';
import { getReduxStore } from './store';

let _cameraIdGroups = [];
let _cameraGroups = [];
let _locationRepresentCameraGroups = [];
// An empty list cannot tell "still loading" from "no floor plans". Floor plans come from this fetch.
let _hasSettledLocationRepCameraGroups = false;

function mapIdsToCameras({ cameraIds, floorPlan }) {
  const allCamerasDict = LocationStore.getAllLocationCamerasDict();
  const newCameras = [];
  const floorPlanCameras = get(floorPlan, 'cameras');
  let floorPlanCamerasDict = {};

  if (!isEmpty(floorPlanCameras)) {
    floorPlanCamerasDict = CommonUtil.mapCollectionToObject({
      collection: floorPlanCameras,
      key: 'id',
      clone: true,
    });
  }

  if (!isEmpty(allCamerasDict)) {
    for (const id of cameraIds) {
      const camera = allCamerasDict[id];
      const floorPlanCamera = floorPlanCamerasDict[id];

      if (camera) {
        if (floorPlanCamera) {
          const { x, y } = floorPlanCamera;
          camera.x = x;
          camera.y = y;
        }

        newCameras.push(camera);
      }
    }
  }

  return newCameras;
}

function mapCameraGroups(idGroups) {
  const locations = LocationStore.getLocations();
  if (isEmpty(locations)) {
    return [];
  }

  const locale = LocaleUtil.getLocale(getReduxStore().getState().localeState.localeCode);
  const nonDefaultGroups = [];
  let favoriteGroup, recentGroup;

  for (const group of idGroups) {
    const { cameraIds, floorPlan, name, ownerType } = group;
    const cameras = mapIdsToCameras({ cameraIds, floorPlan });

    const isSharedGroup = CameraUtil.isSharedCameraGroup(ownerType);
    if (isSharedGroup) {
      if (FeaturesUtil.canAccessSharedCameraGroups()) {
        const notARealGroup = cameras.length <= 1;
        if (notARealGroup && !FeaturesUtil.canCreateSharedCameraGroups()) {
          // if the user can't share camera groups
          // should hide the empty or 1-camera shared groups (CallHome actually filtered the empty shared groups already)
          continue;
        }
      } else {
        // if the user doesn't have access to shared camera groups
        // hide the shared camera group
        continue;
      }
    }

    let translation;

    if (CameraUtil.isFavoriteCameraGroup(name)) {
      translation = locale.camera.favourites;
      favoriteGroup = { ...group, cameras, translation };
    } else if (CameraUtil.isRecentCameraGroup(name)) {
      translation = locale.camera.recent;
      recentGroup = { ...group, cameras, translation };
    } else {
      nonDefaultGroups.push({ ...group, cameras, translation });
    }
  }

  const orderedNonDefaultGroups = orderBy(nonDefaultGroups, ['name'], ['asc']);
  const orderedGroups = concat(
    recentGroup ? [recentGroup] : [],
    favoriteGroup ? [favoriteGroup] : [],
    orderedNonDefaultGroups,
  );
  return orderedGroups;
}

function updateCameraGroupsTranslation(groups, locale) {
  const favorites = CameraUtil.getFavoriteCameraGroup(groups);
  const recent = CameraUtil.getRecentCameraGroup(groups);

  if (favorites) {
    favorites.translation = locale.camera.favourites;
  }

  if (recent) {
    recent.translation = locale.camera.recent;
  }
}

/**
 * Given an updated camera, update all the properties in the store as necessary.
 * @param {Object} updatedCamera
 * @returns {boolean} true if any updates occured
 */
function updateCameraDataInStore(updatedCamera) {
  let storeDataChanged = false;
  const id = get(updatedCamera, 'id');
  const locationId = get(updatedCamera, 'deviceId');

  if (!updatedCamera || !id || !locationId) {
    return;
  }

  const updateTargetCamera = (targetCamera) => {
    targetCamera.isMotionAlarmEnabled = updatedCamera.isMotionAlarmEnabled;
    targetCamera.motionAlarmScheduleId = updatedCamera.motionAlarmScheduleId;
    if (updatedCamera.motionAlarmScheduleId) {
      targetCamera.motionAlarmSchedule = updatedCamera.motionAlarmSchedule;
    } else {
      unset(targetCamera, 'motionAlarmSchedule');
    }
    storeDataChanged = true;
  };

  // Update camera in _cameraGroups
  _cameraGroups.forEach((cameraGroup) => {
    const targetCamera = cameraGroup.cameras.find((camera) => camera.id === id);
    if (targetCamera) {
      updateTargetCamera(targetCamera);
    }
  });

  return storeDataChanged;
}

const CameraStore = assign({}, BaseStore, {
  /**
   * Get the application state
   * @return {object} application state
   */
  getAppState() {
    return {
      cameraGroups: _cameraGroups,
      locationRepCameraGroups: _locationRepresentCameraGroups,
    };
  },

  getCameraIdGroups() {
    return _cameraIdGroups;
  },

  getCameraGroups() {
    return _cameraGroups;
  },

  getLocationRepCameraGroups() {
    return _locationRepresentCameraGroups;
  },

  /** True once the location-representative camera group fetch succeeds or fails. */
  getHasSettledLocationRepCameraGroups() {
    return _hasSettledLocationRepCameraGroups;
  },

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  getBestStreamID(location, cameraId, startTime, endTime) {
    const camArray = location.cameras.filter((cam) => cam.cameraId === cameraId);
    if (camArray.length !== 1) {
      return null;
    }

    const { streams } = camArray[0];
    const sortedStreams = CameraUtil.sortStreamsByQuality({ streams }); // descending by quality

    for (let i = 0; i < sortedStreams.length; i++) {
      // return the fastest stream that has video
      const stream = sortedStreams[i];
      if (parseInt(stream.earliestSegmentDate) < startTime) {
        //earliestSegmentDate can be string
        return stream.id;
      }
    }

    return null;
  },
});

CameraStore.dispatchToken = AppDispatcher.register((action) => {
  switch (action.type) {
    case constants.GET_LATEST_MOTION_SCHEDULE_INFO: {
      AppDispatcher.waitFor([LocationStore.dispatchToken]);
      let storeDataChanged = false;
      const cameras = get(action, 'location.cameras', []);
      cameras.forEach((updatedCamera) => {
        storeDataChanged = updateCameraDataInStore(updatedCamera);
      });
      if (storeDataChanged) {
        CameraStore.emitChange();
      }
      break;
    }

    case constants.UPDATE_CAMERA_MOTION_ALARM_DATA: {
      AppDispatcher.waitFor([LocationStore.dispatchToken]);
      let storeDataChanged = false;
      const updatedCamera = get(action, 'camera');
      storeDataChanged = updateCameraDataInStore(updatedCamera);
      if (storeDataChanged) {
        CameraStore.emitChange();
      }
      break;
    }

    case constants.UPDATE_LOCATION_MOTION_ALARM_SCHEDULE: {
      AppDispatcher.waitFor([LocationStore.dispatchToken]);
      let storeDataChanged = false;
      const updatedCamera = get(action, 'camera');
      const updatedLocationId = get(action, 'locationId');
      const updatedLocationMotionAlarmSchedule = get(action, 'schedule');
      const updatedLocationMotionAlarmScheduleId = get(action, 'schedule.id');

      if (updatedCamera) {
        storeDataChanged = updateCameraDataInStore(updatedCamera);
      }

      const updateTargetLocation = (targetLocation) => {
        return {
          ...targetLocation,
          motionAlarmScheduleId: updatedLocationMotionAlarmScheduleId,
          motionAlarmSchedule: updatedLocationMotionAlarmScheduleId
            ? updatedLocationMotionAlarmSchedule
            : undefined,
        };
      };

      // Update the location property of any camera belonging to the updated location
      const updatedCameraGroups = _cameraGroups.map((cameraGroup) => {
        const updatedCameras = cameraGroup.cameras.map((camera) => {
          if (camera.location.id === updatedLocationId) {
            return {
              ...camera,
              location: updateTargetLocation(camera.location),
            };
          }
          return camera;
        });
        return {
          ...cameraGroup,
          cameras: updatedCameras,
        };
      });

      _cameraGroups = updatedCameraGroups;

      if (storeDataChanged) {
        CameraStore.emitChange();
      }
      break;
    }

    case constants.MAP_CAMERA_GROUPS: {
      AppDispatcher.waitFor([LocationStore.dispatchToken]);
      _cameraGroups = mapCameraGroups(_cameraIdGroups);

      CameraStore.emitChange();
      break;
    }

    case constants.CAMERA_AI_UPDATED_SUCCESS:
    case constants.CAMERA_MOTION_UPDATED_SUCCESS:
    case constants.CAMERA_NAME_UPDATED_SUCCESS: {
      AppDispatcher.waitFor([LocationStore.dispatchToken]);

      const { uniqueId, name, ai, motionParams } = action.payload;

      for (const group of _cameraGroups) {
        const { cameras } = group;
        const camera = cameras.find((camera) => camera.id === uniqueId);

        if (camera) {
          if (name) {
            camera.name = name;
          }
          if (ai) {
            camera.ai = ai;
          }
          if (motionParams) {
            camera.motionParams = motionParams;
          }

          CameraStore.emitChange();
          break;
        }
      }

      break;
    }

    case constants.CAMERA_PTZ_ENABLED_UPDATED_SUCCESS: {
      AppDispatcher.waitFor([LocationStore.dispatchToken]);

      const { uniqueId, isPtzEnabled } = action.payload;

      // A camera can appear in multiple groups as different object instances (e.g. after a
      // single-group remap in the UPDATE_CAMERA_GROUP case above pulls a fresh reference for
      // just that group while other groups keep an older one) — update every match, not just
      // the first, so no group is left showing a stale toggle.
      let didUpdate = false;
      for (const group of _cameraGroups) {
        const { cameras } = group;
        const camera = cameras.find((camera) => camera.id === uniqueId);

        if (camera) {
          camera.isPtzEnabled = isPtzEnabled;
          didUpdate = true;
        }
      }

      if (didUpdate) {
        CameraStore.emitChange();
      }

      break;
    }

    case constants.CAMERA_ROI_UPDATED_SUCCESS: {
      AppDispatcher.waitFor([LocationStore.dispatchToken]);
      const { uniqueCameraId, regions } = action.payload;
      for (const group of _cameraGroups) {
        const { cameras } = group;
        const camera = cameras.find((camera) => camera.id === uniqueCameraId);
        if (camera) {
          camera.regions = [...regions];
          CameraStore.emitChange();
          break;
        }
      }
      break;
    }

    case constants.CAMERA_TAGS_UPDATED_SUCCESS: {
      AppDispatcher.waitFor([LocationStore.dispatchToken]);
      const { cameraId, tags } = action.payload;
      for (const group of _cameraGroups) {
        const { cameras } = group;
        const camera = cameras.find((camera) => camera.id === cameraId);
        if (camera) {
          camera.cameraTagIds = [...tags];
          CameraStore.emitChange();
          break;
        }
      }
      break;
    }

    case constants.UPDATE_LOCALE: {
      const { payload: localeCode } = action;
      const locale = getLocale(localeCode);
      updateCameraGroupsTranslation(_cameraGroups, locale);
      CameraStore.emitChange();
      break;
    }

    case constants.UPDATE_CAMERA_GROUP: {
      const { payload: newGroup } = action;
      const { id, cameraIds, floorPlan } = newGroup;

      _cameraIdGroups = _cameraIdGroups.map((group) => (group.id === id ? newGroup : group));

      _cameraGroups = _cameraGroups.map((group) =>
        group.id === id
          ? { ...newGroup, cameras: mapIdsToCameras({ cameraIds, floorPlan }) }
          : group,
      );

      _locationRepresentCameraGroups = _locationRepresentCameraGroups.map((group) =>
        group.id === id ? newGroup : group,
      );

      CameraStore.emitChange();
      break;
    }

    case constants.UPDATE_LOCATION_REP_CAMERA_GROUPS:
      _locationRepresentCameraGroups = action.payload;
      _hasSettledLocationRepCameraGroups = true;

      CameraStore.emitChange();
      break;

    case constants.LOCATION_REP_CAMERA_GROUPS_FAILED:
      _hasSettledLocationRepCameraGroups = true;

      CameraStore.emitChange();
      break;

    case constants.UPDATE_CAMERA_GROUPS:
      _cameraIdGroups = cloneDeep(action.payload);
      _cameraGroups = mapCameraGroups(action.payload);

      CameraStore.emitChange();
      break;

    case constants.CREATE_CAMERA_GROUP: {
      const { type } = action.payload;

      if (type === constants.CAMERA_GROUP_TYPE_FLOORPLAN) {
        _locationRepresentCameraGroups = [..._locationRepresentCameraGroups, action.payload];
      } else {
        _cameraIdGroups = [..._cameraIdGroups, action.payload];
        _cameraGroups = mapCameraGroups(_cameraIdGroups);
      }
      CameraStore.emitChange();
      break;
    }

    case constants.DELETE_CAMERA_GROUP: {
      const { payload: deletedId } = action;
      _cameraIdGroups = _cameraIdGroups.filter((group) => group.id !== deletedId);
      _cameraGroups = _cameraGroups.filter((group) => group.id !== deletedId);
      CameraStore.emitChange();
      break;
    }
  }
});

export default CameraStore;
